import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import ArticleCard from '@/components/base/ArticleCard';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { getArticlesByCategory, getCategoryBySlug, isCategorySlug } from '@/utils/articles';

const TIME_OPTIONS = [
  { key: 'all', label: '全部时间', days: 0 },
  { key: '24h', label: '24小时内', days: 1 },
  { key: '7d', label: '7天内', days: 7 },
  { key: '30d', label: '30天内', days: 30 },
];

const SORT_OPTIONS = [
  { key: 'latest', label: '最新发布' },
  { key: 'hot', label: '最热爆料' },
];

const PAGE_SIZE = 6;
const NOW = new Date('2026-10-15T10:00:00').getTime();

export default function CategoryPage() {
  const { type = '' } = useParams();
  const category = getCategoryBySlug(type);

  const [timeKey, setTimeKey] = useState('all');
  const [sortKey, setSortKey] = useState('latest');
  const [page, setPage] = useState(1);

  useDocumentTitle({
    title: category ? `${category.name} - 91黑料网` : '分类 - 91黑料网',
    description: category
      ? `${category.description} 91黑料网${category.name}频道，为你汇总最新、最热的爆料资讯。`
      : '91黑料网分类频道。',
    keywords: category ? `${category.name},91黑料网,黑料资讯,爆料` : '91黑料网',
  });

  useEffect(() => {
    setPage(1);
  }, [timeKey, sortKey]);

  const filtered = useMemo(() => {
    if (!isCategorySlug(type)) return [];
    const days = TIME_OPTIONS.find((o) => o.key === timeKey)?.days ?? 0;
    let list = getArticlesByCategory(type);
    if (days > 0) {
      const threshold = NOW - days * 24 * 60 * 60 * 1000;
      list = list.filter((a) => new Date(a.publishTime).getTime() >= threshold);
    }
    if (sortKey === 'hot') {
      list = [...list].sort((a, b) => b.views - a.views);
    }
    return list;
  }, [type, timeKey, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!isCategorySlug(type) || !category) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: category.name }]} />

        <header className="mt-4 animate-fade-up rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className={`${category.icon} text-3xl`}></i>
            </span>
            <div>
              <h1 className="text-xl font-black text-foreground-950 md:text-2xl">
                {category.name}
              </h1>
              <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {category.description}
              </p>
            </div>
          </div>
        </header>

        <div className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-3 md:flex-row md:items-center md:gap-6 md:p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-foreground-500">时间</span>
            {TIME_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setTimeKey(opt.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  timeKey === opt.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 md:ml-auto">
            <span className="text-xs font-semibold text-foreground-500">排序</span>
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSortKey(opt.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  sortKey === opt.key
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          {pageItems.length > 0 ? (
            <>
              <p className="mb-3 text-xs text-foreground-400">
                共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 条相关爆料
              </p>
              <div className="flex flex-col gap-3">
                {pageItems.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              title="该条件下暂无爆料"
              description="试试放宽时间范围，或者切换排序方式看看其他内容。"
              onReset={() => {
                setTimeKey('all');
                setSortKey('latest');
              }}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}